// KnockoutOverviewSections.js
// Presentational sections + shared styles/helpers for the Knockout overview,
// extracted from Knockout.js to keep that screen under the line budget. These
// are pure, prop-driven components — no data fetching happens here. The overview
// shell (Knockout.js) owns all state/loading and renders these.

import React, { useState, useEffect } from "react";
import treeIcon from "../assets/tree-structure.svg";
import trophyFill from "../assets/trophy-fill.svg";

const ACCENT = "#A68CF2"; // knockout accent — matches the Battles card + iOS

// ─── Next-league date (anchor 2026-05-09, every 14 days) ──────────────────────
// Display only; the live slot/number come off the pool documents.

const KNOCKOUT_ANCHOR = new Date(2026, 4, 9); // May 9, 2026 (local — display only)

function nextLeagueDate() {
  const dayMs = 24 * 60 * 60 * 1000;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const anchor = new Date(KNOCKOUT_ANCHOR);
  anchor.setHours(0, 0, 0, 0);
  const daysSince = Math.max(0, Math.round((today - anchor) / dayMs));
  const cyclesPassed = Math.floor(daysSince / 14);
  let candidate = new Date(anchor.getTime() + cyclesPassed * 14 * dayMs);
  if (candidate < today) candidate = new Date(candidate.getTime() + 14 * dayMs);
  return candidate;
}

function nextLeagueDateShort() {
  return nextLeagueDate()
    .toLocaleDateString("en-US", { month: "short", day: "numeric" })
    .toUpperCase();
}

function estimateAgeText(lastUpdated) {
  if (!lastUpdated) return "Updates every 10 min";
  const minutes = Math.floor((Date.now() - lastUpdated.getTime()) / 60000);
  if (minutes < 2) return "Updated just now";
  return `Updated ${minutes} min ago`;
}

// ─── Button state (read-derived, same logic as iOS currentButtonState) ────────

function canDraftToday(pool, standing) {
  if (standing) return standing.status === "alive" && !standing.hasDraftedToday;
  return pool.currentDay === 1; // a user with no standing can only join on Day 1
}

function currentButtonState(pool, day, standing) {
  if (!pool || !day || day.status !== "open") return "notPlaying";
  if (standing && standing.status === "eliminated") return "eliminated";
  if (standing && standing.hasDraftedToday) return "drafted";
  if (!canDraftToday(pool, standing)) return "notPlaying";
  return "draftNow";
}

function SectionHeader({ label, color }) {
  return (
    <div style={styles.sectionHeader}>
      <div style={{ ...styles.rule, background: `linear-gradient(to left, ${color}8c, transparent)` }} />
      <span style={{ ...styles.sectionLabel, color }}>{label}</span>
      <div style={{ ...styles.rule, background: `linear-gradient(to right, ${color}8c, transparent)` }} />
    </div>
  );
}

// ─── 1. Last Pool ─────────────────────────────────────────────────────────────

export function LastPoolSection({ pool }) {
  return (
    <div style={styles.section}>
      <SectionHeader label="LAST POOL" color={ACCENT} />
      <div style={{ ...styles.card, padding: "10px 12px", alignItems: "center" }}>
        <div style={styles.lastWinnerRow}>
          <span style={{ ...styles.trophyGlyph, background: ACCENT }} />
          <span style={styles.lastWinnerName}>{pool.winnerUsername || "—"}</span>
        </div>
        <span style={styles.lastWinnerLabel}>
          {pool.displayTitle.toUpperCase()} CHAMPION
        </span>
      </div>
    </div>
  );
}

// ─── 2. Current Pool ──────────────────────────────────────────────────────────

export function CurrentPoolSection({ pool, meta, day, standing, expanded, onToggleStandings }) {
  const userScore =
    standing && standing.status === "alive" && standing.hasDraftedToday
      ? standing.currentDayScore
      : null;

  const started =
    pool.totalEntrants > 0
      ? `${pool.totalEntrants}`
      : day && day.usersSubmitted > 0
      ? `${day.usersSubmitted}`
      : "—";

  const estCut =
    day && day.estimatedCutScore && day.estimatedCutScore > 0
      ? `${day.estimatedCutScore}`
      : "—";

  const showStandings = pool.usersAlive > 0 && pool.usersAlive <= 50;

  return (
    <div style={styles.section}>
      <SectionHeader label="CURRENT POOL" color={meta.hex} />
      <div style={styles.card}>
        {/* Pool title + day */}
        <div style={styles.currentTitleRow}>
          <span style={{ ...styles.currentTitle, color: meta.hex }}>
            {pool.displayTitle.toUpperCase()}
          </span>
          <span style={styles.currentDot}>·</span>
          <span style={{ ...styles.currentTitle, color: `${meta.hex}b3` }}>
            DAY {pool.currentDay}
          </span>
        </div>

        <Divider />

        <StatusBar standing={standing} />

        <Divider />

        {/* Stat cells */}
        <div style={styles.statRow}>
          <StatCell value={started} label="STARTED" />
          <StatDivider />
          <StatCell value={`${pool.usersAlive}`} label="ALIVE" />
          <StatDivider />
          <StatCell value={estCut} label="EST. CUT" color={meta.hex} />
          <StatDivider />
          {userScore != null ? (
            <StatCell
              value={userScore > 0 ? `${userScore}` : "000"}
              label="MY SCORE"
              color={userScore > 0 ? meta.hex : "rgba(255,80,80,0.85)"}
            />
          ) : (
            <StatCell value="—" label="MY SCORE" />
          )}
        </div>

        {/* Today's Top Scores (read-only expander) */}
        {showStandings && (
          <>
            <Divider faint />
            <StandingsExpander
              meta={meta}
              expanded={expanded}
              onToggle={onToggleStandings}
            />
          </>
        )}

        {day && (
          <>
            <Divider faint />
            <div style={styles.estimateAge}>{estimateAgeText(day.lastEstimateUpdatedAt)}</div>
          </>
        )}
      </div>
    </div>
  );
}

function StatusBar({ standing }) {
  let label = "DNP";
  let color = "rgba(255,255,255,0.35)";
  let bg = "rgba(255,255,255,0.06)";
  if (standing) {
    if (standing.status === "alive") {
      label = "ALIVE";
      color = "rgba(90,220,120,0.85)";
      bg = "rgba(90,220,120,0.15)";
    } else if (standing.status === "eliminated") {
      label = "KNOCKED OUT";
      color = "rgba(255,90,90,0.8)";
      bg = "rgba(255,90,90,0.12)";
    } else if (standing.status === "winner") {
      label = "WINNER 🏆";
      color = ACCENT;
      bg = `${ACCENT}2e`;
    }
  }
  return (
    <div style={{ ...styles.statusBar, color, background: bg }}>{label}</div>
  );
}

function StandingsExpander({ meta, expanded, onToggle }) {
  const open = expanded?.open;
  const loading = expanded?.loading;
  const list = expanded?.list;
  return (
    <div>
      <button onClick={onToggle} style={styles.standingsToggle}>
        <span style={styles.standingsToggleLabel}>TODAY'S TOP SCORES</span>
        <span style={styles.standingsChevron}>
          {loading ? "…" : open ? "▲" : "▼"}
        </span>
      </button>
      {open && (
        <div style={styles.standingsBody}>
          {loading ? null : !list || list.length === 0 ? (
            <div style={styles.standingsEmpty}>No scores submitted yet today</div>
          ) : (
            <div style={styles.standingsList}>
              {list.map((row) => (
                <div
                  key={`${row.rank}-${row.username}`}
                  style={{
                    ...styles.standingRow,
                    background: row.isMe ? `${meta.hex}14` : "transparent",
                  }}
                >
                  <span
                    style={{
                      ...styles.standingRank,
                      color: row.rank <= 3 ? meta.hex : "rgba(255,255,255,0.3)",
                    }}
                  >
                    {row.rank}
                  </span>
                  <span
                    style={{
                      ...styles.standingName,
                      fontWeight: row.isMe ? 700 : 500,
                    }}
                  >
                    {row.username}
                  </span>
                  {row.isMe && (
                    <span style={{ ...styles.youPill, color: meta.hex, background: `${meta.hex}26` }}>
                      YOU
                    </span>
                  )}
                  <span style={styles.standingScore}>{row.score}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 3. Next Pool ─────────────────────────────────────────────────────────────

export function NextPoolSection({ registrantCount, registered }) {
  return (
    <div style={styles.section}>
      <SectionHeader label="NEXT POOL" color={ACCENT} />
      <div
        style={{
          ...styles.card,
          flexDirection: "row",
          padding: "12px 0",
          border: `1px solid ${registered ? `${ACCENT}59` : `${ACCENT}33`}`,
          background: `${ACCENT}0f`,
        }}
      >
        <div style={styles.nextCol}>
          <span style={styles.nextBig}>{registrantCount}</span>
          <span style={styles.nextSmall}>REGISTERED</span>
        </div>
        <div style={styles.nextVDivider} />
        <div style={styles.nextCol}>
          <span style={{ ...styles.nextBig, fontSize: 20, color: registered ? ACCENT : "#fff" }}>
            {nextLeagueDateShort()}
          </span>
          <span style={{ ...styles.nextSmall, color: registered ? `${ACCENT}b3` : "rgba(255,255,255,0.4)" }}>
            {registered ? "REMINDER SET" : "NEXT KNOCKOUT"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── How To Play (only when no active pool) ───────────────────────────────────

export function RulesCard({ meta }) {
  const rules = [
    "All users draft from an identical player pool",
    "90 second timer to complete your draft",
    "No Rating Reveal or Player Refresh",
    "Top 50% of scores advance to the next day",
    "Ends with a 1v1 Final to crown a champion",
    "New pools begin every Saturday",
  ];
  return (
    <div style={styles.section}>
      <SectionHeader label="HOW TO PLAY" color={meta.hex} />
      <div style={{ ...styles.card, padding: 16, gap: 10, alignItems: "stretch" }}>
        {rules.map((r) => (
          <div key={r} style={styles.ruleRow}>
            <span style={{ ...styles.ruleDot, background: `${meta.hex}b3` }} />
            <span style={styles.ruleText}>{r}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Pinned buttons (inert in Phase A) ────────────────────────────────────────

export function PinnedButtons({
  pools,
  poolData,
  registered,
  registering,
  onRegisterToggle,
  startingDraft,
  draftError,
  onDraftNow,
}) {
  const pool = [...pools].sort((a, b) => b.poolNumber - a.poolNumber)[0] || null;
  const data = pool ? poolData[pool.id] : null;
  const state = currentButtonState(pool, data?.day, data?.standing);

  const draftLabel = {
    draftNow: "DRAFT NOW",
    drafted: "DRAFTED ✓",
    eliminated: "KNOCKED OUT",
    notPlaying: "NOT PLAYING",
  }[state];

  const draftActive = state === "draftNow" && !startingDraft;
  const draftStyle = draftActive ? styles.pinnedButtonActive : styles.pinnedButtonDisabled;

  // REGISTER state: active (filled) when not registered, highlighted (outline)
  // when registered, dimmed while a write is in flight.
  const regStyle = registering
    ? styles.pinnedButtonDisabled
    : registered
    ? styles.pinnedButtonHighlighted
    : styles.pinnedButtonActive;
  const regLabel = registered
    ? `REGISTERED\n${nextLeagueDateShort()}`
    : `REGISTER\n${nextLeagueDateShort()}`;

  return (
    <div style={styles.pinned}>
      <div style={styles.pinnedRule} />
      <div style={styles.pinnedRow}>
        {/* DRAFT NOW — live (only clickable in the draftNow state) */}
        <button
          onClick={state === "draftNow" ? onDraftNow : undefined}
          disabled={state !== "draftNow" || startingDraft}
          style={{
            ...styles.pinnedButton,
            border: "none",
            padding: 0,
            ...draftStyle,
            cursor: draftActive ? "pointer" : "default",
          }}
        >
          {startingDraft ? "…" : draftLabel}
        </button>

        {/* REGISTER — live */}
        <button
          onClick={onRegisterToggle}
          disabled={registering}
          style={{
            ...styles.pinnedButton,
            border: "none",
            padding: 0,
            ...regStyle,
            cursor: registering ? "default" : "pointer",
          }}
        >
          {registering ? "…" : regLabel}
        </button>
      </div>
      {draftError && <div style={styles.draftError}>{draftError}</div>}
    </div>
  );
}

// ─── Guest teaser ─────────────────────────────────────────────────────────────

export function GuestTeaser({ onSignIn }) {
  const [appeared, setAppeared] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAppeared(true), 60);
    return () => clearTimeout(t);
  }, []);
  const features = [
    "Daily elimination tournaments across all 5 sports",
    "Everyone drafts the same player pool — pure skill",
    "Survive the cut each night; bottom 50% are knocked out",
    "Outlast the field to be crowned champion",
  ];
  return (
    <div style={styles.container}>
      <div
        style={{
          ...styles.titleRow,
          opacity: appeared ? 1 : 0,
          transform: appeared ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 0.4s ease, transform 0.4s ease",
        }}
      >
        <span style={{ ...styles.treeIcon, transform: "rotate(180deg)" }} />
        <span style={styles.title}>KNOCKOUT MODE</span>
        <span style={{ ...styles.treeIcon, transform: "rotate(180deg)" }} />
      </div>

      <div
        style={{
          ...styles.section,
          marginTop: 8,
          opacity: appeared ? 1 : 0,
          transform: appeared ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 0.5s ease 0.08s, transform 0.5s ease 0.08s",
        }}
      >
        <div style={{ ...styles.card, padding: 20, gap: 14, alignItems: "stretch", background: `${ACCENT}0f`, border: `1px solid ${ACCENT}33` }}>
          <span style={styles.teaserHeadline}>SURVIVAL LEAGUES</span>
          {features.map((f) => (
            <div key={f} style={styles.ruleRow}>
              <span style={{ ...styles.ruleDot, background: ACCENT }} />
              <span style={styles.ruleText}>{f}</span>
            </div>
          ))}
          <span style={styles.teaserNote}>
            Knockout is for registered players — sign in to join a pool.
          </span>
          <button onClick={onSignIn} style={styles.signInButton}>
            SIGN IN TO PLAY
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Small shared pieces ──────────────────────────────────────────────────────

function Divider({ faint }) {
  return (
    <div
      style={{
        height: 1,
        margin: "0 16px",
        background: `rgba(255,255,255,${faint ? 0.06 : 0.08})`,
      }}
    />
  );
}
function StatDivider() {
  return <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.1)" }} />;
}
function StatCell({ value, label, color = "#fff" }) {
  return (
    <div style={styles.statCell}>
      <span style={{ ...styles.statValue, color }}>{value}</span>
      <span style={styles.statLabel}>{label}</span>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

export const styles = {
  container: {
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "12px 16px 32px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  titleRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" },
  championsWrap: {
    // Mirrors the leaderboard trophy placement: fixed + centered to the same
    // 480px column as the TopBar so the button lands in its empty right slot.
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    maxWidth: "480px",
    margin: "0 auto",
    height: 0,
    pointerEvents: "none",
    zIndex: 40,
  },
  championsBtn: {
    position: "absolute",
    top: "14px",   // matches the TopBar's top padding (aligns with the menu button)
    right: "16px", // matches the TopBar's horizontal padding
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: `1px solid ${ACCENT}59`,
    background: `${ACCENT}1f`,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
  },
  championsIcon: {
    width: 18,
    height: 18,
    display: "inline-block",
    background: ACCENT,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  },
  treeIcon: {
    width: 26,
    height: 26,
    flexShrink: 0,
    background: ACCENT,
    WebkitMaskImage: `url(${treeIcon})`,
    maskImage: `url(${treeIcon})`,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    display: "inline-block",
  },
  title: {
    // Scales with the viewport so it never wraps to two lines on a narrow phone, while
    // staying capped at the original size on wider screens. whiteSpace:nowrap keeps it on
    // one line; the letter-spacing eases off a touch on small screens to help it fit.
    fontSize: "clamp(14px, 5vw, 22px)",
    fontWeight: 900,
    fontStyle: "italic",
    color: "#fff",
    letterSpacing: "clamp(1.2px, 0.5vw, 2.5px)",
    whiteSpace: "nowrap",
  },
  sportSelector: { display: "flex", gap: "8px" },
  sportPill: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    transition: "transform 0.2s ease",
  },
  sportGlyph: {
    width: 24,
    height: 24,
    display: "inline-block",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    maskSize: "contain",
  },
  content: { display: "flex", flexDirection: "column", gap: "12px", transition: "opacity 0.25s ease" },
  section: { display: "flex", flexDirection: "column", gap: "10px" },
  sectionHeader: { display: "flex", alignItems: "center", gap: "10px" },
  rule: { flex: 1, height: 1 },
  sectionLabel: { fontSize: "12px", fontWeight: 800, letterSpacing: "2.5px", whiteSpace: "nowrap" },
  card: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 14,
    background: `${ACCENT}17`,
    border: `1px solid ${ACCENT}47`,
    overflow: "hidden",
  },
  // Last pool
  lastWinnerRow: { display: "flex", alignItems: "center", gap: 6, justifyContent: "center" },
  trophyGlyph: {
    width: 15,
    height: 15,
    flexShrink: 0,
    display: "inline-block",
    // trophy-fill icon, tinted with the knockout accent via mask
    WebkitMaskImage: `url(${trophyFill})`,
    maskImage: `url(${trophyFill})`,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  },
  lastWinnerName: { fontSize: 18, fontWeight: 900, fontStyle: "italic", color: "#fff" },
  lastWinnerLabel: { fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, textAlign: "center" },
  // Current pool
  currentTitleRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0" },
  currentTitle: { fontSize: 14, fontWeight: 600, letterSpacing: 0.3 },
  currentDot: { fontSize: 12, color: "rgba(255,255,255,0.2)" },
  statusBar: { fontSize: 18, fontWeight: 900, letterSpacing: 1.5, textAlign: "center", padding: "8px 0" },
  statRow: { display: "flex", alignItems: "center", padding: "6px 0" },
  statCell: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  statValue: { fontSize: 22, fontWeight: 700 },
  statLabel: { fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1 },
  estimateAge: { fontSize: 10, fontWeight: 500, color: "rgba(255,255,255,0.3)", textAlign: "center", padding: "5px 0" },
  // Standings expander
  standingsToggle: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
  standingsToggleLabel: { fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: "#fff" },
  standingsChevron: { fontSize: 10, color: "rgba(255,255,255,0.35)" },
  standingsBody: { background: `${ACCENT}0a`, maxHeight: 380, overflowY: "auto" },
  standingsEmpty: { fontSize: 11, fontWeight: 500, fontStyle: "italic", color: "rgba(255,255,255,0.3)", textAlign: "center", padding: "12px 16px" },
  standingsList: { display: "flex", flexDirection: "column" },
  standingRow: { display: "flex", alignItems: "center", gap: 10, padding: "9px 16px" },
  standingRank: { fontSize: 11, fontWeight: 700, width: 22, textAlign: "center" },
  standingName: { fontSize: 14, color: "#fff", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  youPill: { fontSize: 8, fontWeight: 700, letterSpacing: 0.8, padding: "2px 5px", borderRadius: 4 },
  standingScore: { fontSize: 14, fontWeight: 700, color: "#fff", fontVariantNumeric: "tabular-nums" },
  // Next pool
  nextCol: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  nextBig: { fontSize: 26, fontWeight: 700, color: "#fff" },
  nextSmall: { fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1 },
  nextVDivider: { width: 1, height: 40, background: "rgba(255,255,255,0.1)" },
  // Rules
  ruleRow: { display: "flex", alignItems: "center", gap: 10 },
  ruleDot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },
  ruleText: { fontSize: 13, color: "rgba(255,255,255,0.6)" },
  // Pinned buttons
  pinned: { display: "flex", flexDirection: "column", marginTop: 4 },
  pinnedRule: { height: 1, background: "rgba(255,255,255,0.07)", marginBottom: 10 },
  pinnedRow: { display: "flex", gap: 10 },
  pinnedButton: {
    flex: 1,
    height: 50,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    whiteSpace: "pre-line",
    userSelect: "none",
  },
  pinnedButtonActive: {
    background: `linear-gradient(to right, ${ACCENT}, ${ACCENT}cc)`,
    color: "#fff",
    boxShadow: `0 5px 10px ${ACCENT}66`,
  },
  pinnedButtonHighlighted: {
    background: `${ACCENT}1f`,
    color: ACCENT,
    border: `1px solid ${ACCENT}73`,
  },
  pinnedButtonDisabled: {
    background: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.25)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  draftError: {
    marginTop: 8,
    fontSize: 11,
    fontWeight: 500,
    color: "rgba(255,90,90,0.85)",
    textAlign: "center",
  },
  // Guest teaser
  teaserHeadline: { fontSize: 16, fontWeight: 900, fontStyle: "italic", color: ACCENT, letterSpacing: 2, textAlign: "center" },
  teaserNote: { fontSize: 12, color: "rgba(255,255,255,0.55)", textAlign: "center", marginTop: 4 },
  signInButton: {
    marginTop: 4,
    height: 48,
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    background: `linear-gradient(to right, ${ACCENT}, ${ACCENT}cc)`,
    color: "#fff",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 1,
    boxShadow: `0 5px 10px ${ACCENT}66`,
  },
};
